/**
 * Shared date/timestamp primitives for response schemas.
 *
 * See docs/claude/timezone-policy.md for the two date categories used across ProjectB:
 *
 * - UTC timestamp (a moment in time — `createdAt`, `updatedAt`, `settledAt`,
 *   `@db.Timestamptz` columns): use `isoDateTimeString`, or its branded sibling
 *   `timestampString` when adopting the compile-time-checked pairing below.
 * - Calendar date (a named day with no timezone — `startDate`, `dueDate`,
 *   `@db.Date` columns, `YYYY-MM-DD`): use `calendarDateString` below — never coerce a
 *   `Date` into this shape via `.toISOString()`. The route/service must produce the
 *   `YYYY-MM-DD` string itself — see `apps/api/src/utils/date.ts`'s `toCalendarDateString`.
 *   Coercing a calendar date via `.toISOString()` produces the wrong string shape and
 *   risks a day shift for a `Date` that wasn't constructed as UTC midnight
 *   (docs/claude/anti-patterns.md AP-002).
 */
import { z } from 'zod';
/**
 * ISO-8601 UTC timestamp response field.
 *
 * Under zod v3 + the old fastify-type-provider-zod, Fastify's `fast-json-stringify`
 * response serializer auto-converted a raw `Date` to its ISO string for a `z.string()`
 * field. zod v4's native serializer validates the response value strictly against the
 * schema before stringifying, so a raw `Date` handed to `z.string()` (or `z.iso.datetime()`)
 * now throws `FST_ERR_RESPONSE_SERIALIZATION` instead of being coerced.
 *
 * Accepting either shape here means a route is correct whether its service already calls
 * `.toISOString()` or passes a Prisma `Date` straight through. The output type (`z.infer`)
 * is always `string`, never `Date` — matching what's actually on the wire.
 */
export const isoDateTimeString = z
    .union([z.string(), z.date()])
    .transform((value) => (value instanceof Date ? value.toISOString() : value));
const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Calendar date response/request field (`YYYY-MM-DD`, no time-of-day) — `startDate`,
 * `dueDate`, `scheduledDate`, `effectiveDate`, `@db.Date` columns.
 *
 * Branded (`z.infer` is `string & z.BRAND<'CalendarDateString'>`) so the two display
 * functions in `packages/shared`'s `timezone.ts` — `formatCalendarDate` and
 * `formatTimestamp` — can require it and its `timestampString` sibling respectively, and
 * reject the other at compile time. That is the actual fix for AP-002
 * (docs/claude/anti-patterns.md): the historical failure mode was always a calendar-date
 * value reaching `formatTimestamp` (or a raw `new Date()`), which no runtime check
 * downstream can catch before it ships — only the type checker, at the call site, can.
 *
 * The regex is a secondary backstop, not the primary defense: it also fails a service that
 * accidentally sends `.toISOString()` output (which doesn't match `YYYY-MM-DD`) through this
 * field, turning that regression into a test failure instead of a silently wrong date.
 */
export const calendarDateString = z
    .string()
    .regex(CALENDAR_DATE_PATTERN, 'Expected a calendar date as YYYY-MM-DD')
    .brand();
/**
 * Branded variant of `isoDateTimeString`, for fields adopting the compile-time-checked
 * pairing with `calendarDateString` above. Not applied to the base `isoDateTimeString`
 * export itself — that would retype every one of its existing (~900) usages in one pass.
 * Migrate a field by swapping its `isoDateTimeString` for this.
 */
export const timestampString = isoDateTimeString.brand();
//# sourceMappingURL=date.js.map